// ---------------------------------------------------------------------------
// Blot helpers
// ---------------------------------------------------------------------------
function isTableCellLine(blot) {
    return blot !== null && blot.statics.blotName === 'table-cell-line';
}
function areBlotsInSameCell(blotA, blotB) {
    return blotA !== null && blotB !== null && blotA.parent === blotB.parent;
}
// ---------------------------------------------------------------------------
// Selection helpers
// ---------------------------------------------------------------------------
/** Returns false (block) if a range selection would cross a table cell boundary. */
function wouldRangeSelectionCrossCellBoundary(quill, range) {
    const [startLine] = quill.getLine(range.index);
    const [endLine] = quill.getLine(range.index + range.length);
    if (isTableCellLine(startLine) || isTableCellLine(endLine)) {
        if (!areBlotsInSameCell(startLine, endLine))
            return true;
    }
    return false;
}
// ---------------------------------------------------------------------------
// Backspace / Delete decision functions
// ---------------------------------------------------------------------------
/**
 * Determines whether a Backspace keystroke should be allowed.
 * Returns true to allow, false to block.
 */
function shouldAllowBackspace(quill, range, context) {
    const [line] = quill.getLine(range.index);
    // Range selection: only allow if start and end are in the same cell
    if (range.length > 0) {
        return !wouldRangeSelectionCrossCellBoundary(quill, range);
    }
    // Collapsed cursor in a table cell at offset 0
    if (line !== null && isTableCellLine(line) && context.offset === 0) {
        if (!line.prev)
            return false;
        if (!isTableCellLine(line.prev))
            return false;
        if (!areBlotsInSameCell(line, line.prev))
            return false;
        return true;
    }
    // Collapsed cursor outside a table — prevent merging into table above
    if (!isTableCellLine(line) && range.index > 0 && context.offset === 0) {
        const [previousLine] = quill.getLine(range.index - 1);
        if (isTableCellLine(previousLine))
            return false;
    }
    return true;
}
/**
 * Determines whether a Delete keystroke should be allowed.
 * Returns true to allow, false to block.
 */
function shouldAllowDelete(quill, range) {
    const [line] = quill.getLine(range.index);
    // Range selection: same cross-cell check as backspace
    if (range.length > 0) {
        return !wouldRangeSelectionCrossCellBoundary(quill, range);
    }
    // Collapsed cursor at end of a table-cell-line
    if (isTableCellLine(line) && line !== null) {
        const lineLength = line.length();
        const [, offset] = quill.getLine(range.index);
        if (offset >= lineLength - 1) {
            if (!line.next)
                return false;
            if (!isTableCellLine(line.next))
                return false;
            if (!areBlotsInSameCell(line, line.next))
                return false;
        }
    }
    // Collapsed cursor outside a table — prevent merging into table below
    if (!isTableCellLine(line)) {
        const nextIndex = range.index + (line !== null ? line.length() : 1);
        if (nextIndex < quill.getLength()) {
            const [nextLine] = quill.getLine(nextIndex);
            if (isTableCellLine(nextLine))
                return false;
        }
    }
    return true;
}
// ---------------------------------------------------------------------------
// Binding registration
// ---------------------------------------------------------------------------
/**
 * Registers a keyboard binding and moves it to the front of Quill's
 * binding list so it executes before other handlers for the same key.
 */
function addPriorityKeyBinding(editor, key, handler) {
    editor.keyboard.addBinding({ key }, {}, handler);
    const bindings = editor.keyboard.bindings[key];
    bindings.unshift(bindings.pop());
}
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Adds keyboard bindings that prevent Backspace and Delete from destroying
 * table cell structure. quill-better-table's built-in bindings only handle
 * collapsed-cursor cases; this covers range selections and cross-cell merges.
 */
export function addTableKeyboardProtection(editor) {
    addPriorityKeyBinding(editor, 'Backspace', function (range, context) {
        return shouldAllowBackspace(this.quill, range, context);
    });
    addPriorityKeyBinding(editor, 'Delete', function (range) {
        return shouldAllowDelete(this.quill, range);
    });
}
